
import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  DirectionsRenderer
} from "@react-google-maps/api";
import mapStyles from "./mapStyles"; // checkout snazzymaps.com

function Map(props) {
    const [markers, setMarkers] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [directions, setDirections] = useState(null);
    const [removeRouteLines, setRemoveRouteLines] = useState(0);

    const [mapInstance, setMapInstance] = useState(null);
    const allRenderers = useRef([]);
    const allMarkers = useRef([]);
    const currentInfoWindow = useRef(null);
    const currentMarker = useRef(null);
    const openInfoWindow = useRef(null);

    // Handle route optimization for selected inspector
    useEffect(() => {
        if (props.selectedInspector && props.optimizedRoutes && mapInstance) {
            const selectedRoute = props.optimizedRoutes.find(
                route => route.assignedInspector.INSPECTOR_ID === props.selectedInspector.INSPECTOR_ID
            );

            if (selectedRoute && selectedRoute.routeOrder && selectedRoute.routeOrder.length > 0) {
                console.log('SELECTED ROUTE', selectedRoute);

                setMarkers([]);

                const directionsService = new window.google.maps.DirectionsService();

                // Clear existing renderers and markers
                allRenderers.current.forEach(renderer => renderer.setMap(null));
                allMarkers.current.forEach(marker => marker.setMap(null));
                allRenderers.current = [];
                allMarkers.current = [];

                let orderedArray = [];
                for (let a = 0; a < selectedRoute.routeOrder.length; a++) {
                    const routePoint = selectedRoute.routeOrder[a];
                    orderedArray.push({
                        label: (a).toString(),
                        address: routePoint.address,
                        coordinates: {
                            lat: routePoint.coordinates[0],
                            lng: routePoint.coordinates[1]
                        },
                        soloUrls: routePoint.soloUrls
                    });
                }

                // If start and end address are same keep same symbol, else mark end as flag
                orderedArray[0] = {
                    label: '◉',
                    address: orderedArray[0].address,
                    coordinates: {
                        lat: orderedArray[0].coordinates.lat,
                        lng: orderedArray[0].coordinates.lng
                    },
                    soloUrls: selectedRoute.routeOrder[0].soloUrls
                };
                if (orderedArray[0].address === orderedArray[orderedArray.length - 1].address) {
                    orderedArray[orderedArray.length - 1] = {
                        label: '◉',
                        address: orderedArray[orderedArray.length - 1].address,
                        coordinates: {
                            lat: orderedArray[orderedArray.length - 1].coordinates.lat,
                            lng: orderedArray[orderedArray.length - 1].coordinates.lng
                        },
                        soloUrls: selectedRoute.routeOrder[selectedRoute.routeOrder.length - 1].soloUrls
                    };
                } else {
                    orderedArray[orderedArray.length - 1] = {
                        label: '⚑',
                        address: orderedArray[orderedArray.length - 1].address,
                        coordinates: {
                            lat: orderedArray[orderedArray.length - 1].coordinates.lat,
                            lng: orderedArray[orderedArray.length - 1].coordinates.lng
                        },
                        soloUrls: selectedRoute.routeOrder[selectedRoute.routeOrder.length - 1].soloUrls
                    };
                }

                console.log('ORDERED ARRAY WITH COORDINATES', orderedArray);

                let currTravelMode = window.google.maps.TravelMode.DRIVING;

                // Get inspection data for a given routeOrder index
                const getInspectionData = (routeOrderIndex) => {
                    // routeOrder index 0 is start location, so inspection data starts at index 1
                    // assignedInspections array is 2 elements shorter than routeOrder (no start/end)
                    const inspectionIndex = routeOrderIndex - 1;
                    if (inspectionIndex >= 0 && inspectionIndex < selectedRoute.assignedInspections.length) {
                        return selectedRoute.assignedInspections[inspectionIndex];
                    }
                    return null;
                };

                // Get all inspections for a given address
                const getAllInspectionsForAddress = (address) => {
                    return selectedRoute.assignedInspections.filter(inspection => {
                        const inspectionAddress = `${inspection.Street} ${inspection.City} ${inspection.State} ${inspection.Postcode}`;
                        return inspectionAddress === address;
                    });
                };



                // Create InfoWindow content
                const createInfoWindowContent = (address, inspection, soloUrls, isStartOrEnd = false) => {
                    if (!inspection || isStartOrEnd) {
                        // Start or end location (no inspection data)
                        const locationType = isStartOrEnd ? (isStartOrEnd === 'start' ? 'Start Location' : 'End Location') : 'Location';
                        return `
                            <div class="p-4 w-96">
                                <div class="font-semibold text-lg text-blue-500 mb-2">${address}</div>
                                <div class="text-sm text-gray-600">${locationType}</div>
                                ${soloUrls ? `
                                    <button onclick="window.open('${soloUrls.googleUrl}', '_blank')" 
                                            class="mt-3 w-full bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors">
                                        Open in Maps
                                    </button>
                                ` : ''}
                            </div>
                        `;
                    }

                    // Check if there are multiple inspections at this address
                    const allInspectionsAtAddress = getAllInspectionsForAddress(address);
                    const hasMultipleInspections = allInspectionsAtAddress.length > 1;

                    // If multiple inspections, show the expanded view directly
                    if (hasMultipleInspections) {
                        return createExpandedInfoWindowContent(address, allInspectionsAtAddress, soloUrls);
                    }

                    // Single inspection - show detailed view
                    // Format date and times
                    const formatDate = (dateString) => {
                        return dateString.split(' ')[0]; // Get just the date part
                    };
                    
                    const formatTime = (dateTimeString) => {
                        return dateTimeString.split(' ')[1].substring(0, 5); // Get HH:MM format
                    };
                    
                    const appointmentTimes = `${formatTime(inspection.newAppointmentStart)} - ${formatTime(inspection.newAppointmentFinish)}`;
                    const scheduledStatus = inspection.Status === "Pending" ? "Automated" : "Manual";
                    const daysLeftColor = inspection.daysLeft < 0 ? "text-red-600" : "text-green-600";
                    
                    return `
                        <div class="p-4 w-96">
                            <div class="font-semibold text-lg text-blue-500 mb-3">${address}</div>
                            <div class="space-y-2 mb-4">
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Appointment Time:</span>
                                    <span class="text-sm font-medium text-gray-900">${appointmentTimes}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Scheduled:</span>
                                    <span class="text-sm font-medium text-gray-900 italic">${scheduledStatus}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Inspection ID:</span>
                                    <span class="text-sm font-medium text-gray-900">${inspection.INSPECTION_ID}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Record ID:</span>
                                    <span class="text-sm font-medium text-gray-900">${inspection.Record_ID}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Record Open Date:</span>
                                    <span class="text-sm font-medium text-gray-900">${formatDate(inspection.Record_Open_Date)}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Priority:</span>
                                    <span class="text-sm font-medium text-gray-900">${inspection.Priority}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Days Left:</span>
                                    <span class="text-sm font-medium ${daysLeftColor}">${inspection.daysLeft}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Task Type:</span>
                                    <span class="text-sm font-medium text-gray-900">${inspection.TaskType}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Unit:</span>
                                    <span class="text-sm font-medium text-gray-900">${inspection.UNIT}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-sm text-gray-600">Community District:</span>
                                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">${inspection.District}</span>
                                </div>
                            </div>
                            
                            <div class="mb-4">
                                <div class="text-sm text-gray-600 mb-1">Complaint Category:</div>
                                <div class="text-sm text-gray-900 bg-gray-50 p-2 rounded">${inspection.ComplaintCategory}</div>
                            </div>
                            
                            ${soloUrls ? `
                                <button onclick="window.open('${soloUrls.googleUrl}', '_blank')" 
                                        class="w-full bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors">
                                    Open in Maps
                                </button>
                            ` : ''}
                        </div>
                    `;
                };

                // Create expanded info window content showing all inspections
                const createExpandedInfoWindowContent = (address, allInspections, soloUrls) => {
                    const formatDate = (dateString) => {
                        return dateString.split(' ')[0];
                    };
                    
                    const formatTime = (dateTimeString) => {
                        return dateTimeString.split(' ')[1].substring(0, 5);
                    };

                    // Get community district from first inspection (should be same for all)
                    const communityDistrict = allInspections[0]?.District || '';

                    let content = `
                        <div class="p-4 w-96 max-h-96 overflow-y-auto">
                            <div class="font-semibold text-lg text-blue-500 mb-2">${address}</div>
                            <div class="mb-3 flex items-center gap-2">
                                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    ${allInspections.length} inspection${allInspections.length > 1 ? 's' : ''} at this location
                                </span>
                                ${communityDistrict ? `
                                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        ${communityDistrict}
                                    </span>
                                ` : ''}
                            </div>
                            <div class="space-y-4">
                    `;

                    allInspections.forEach((inspection, index) => {
                        const appointmentTimes = `${formatTime(inspection.newAppointmentStart)} - ${formatTime(inspection.newAppointmentFinish)}`;
                        const scheduledStatus = inspection.Status === "Pending" ? "Automated" : "Manual";
                        const daysLeftColor = inspection.daysLeft < 0 ? "text-red-600" : "text-green-600";
                        
                        content += `
                            <div class="border border-gray-200 hover:border-gray-400 rounded-lg p-3 ${index > 0 ? 'mt-3' : ''}">
                                <div class="flex justify-between items-start mb-2">
                                    <span class="text-sm font-medium text-gray-900">Inspection ${index + 1}<span class="mx-1"> | </span><span class="italic text-xs text-gray-600"> ${inspection.UNIT}</span></span>
                                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">${inspection.Priority}</span>
                                </div>
                                <div class="space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Time:</span>
                                        <span class="font-medium">${appointmentTimes}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Scheduled:</span>
                                        <span class="font-medium italic">${scheduledStatus}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">ID:</span>
                                        <span class="font-medium">${inspection.INSPECTION_ID}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Record:</span>
                                        <span class="font-medium">${inspection.Record_ID}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Record Open Date:</span>
                                        <span class="font-medium">${formatDate(inspection.Record_Open_Date)}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Days Left:</span>
                                        <span class="font-medium ${daysLeftColor}">${inspection.daysLeft}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600">Type:</span>
                                        <span class="font-medium">${inspection.TaskType}</span>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <div class="text-xs text-gray-600 mb-1">Complaint Category:</div>
                                    <div class="text-xs text-gray-900 bg-gray-50 p-2 rounded">${inspection.ComplaintCategory}</div>
                                </div>
                            </div>
                        `;
                    });

                    content += `
                            </div>
                            ${soloUrls ? `
                                <div class="mt-4">
                                    <button onclick="window.open('${soloUrls.googleUrl}', '_blank')" 
                                            class="w-full bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors">
                                        Open in Maps
                                    </button>
                                </div>
                            ` : ''}
                        </div>
                    `;

                    return content;
                };

                // Define a recursive function to fetch and render segmented routes.
                const fetchSegmentedRoutes = (startIndex = 0) => {
                    if (startIndex >= orderedArray.length - 1) {
                        console.log('All segments processed.');
                        return;
                    }
                
                    const endIndex = Math.min(startIndex + 26, orderedArray.length - 1);
                    const origin = orderedArray[startIndex].coordinates;
                    const destination = orderedArray[endIndex].coordinates;
                    const waypoints = orderedArray.slice(startIndex + 1, endIndex).map(point => ({
                        location: point.coordinates,
                        stopover: true
                    }));
                
                    directionsService.route({
                        origin: origin,
                        destination: destination,
                        travelMode: currTravelMode,
                        waypoints: waypoints
                    }, (result, status) => {
                        if (status === window.google.maps.DirectionsStatus.OK) {

                            const legMarkers = [];
                            result.routes[0].legs.forEach((leg, index) => {
                                const marker = new window.google.maps.Marker({
                                    position: leg.start_location,
                                    label: {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        text: orderedArray[startIndex + index].label,
                                        fontSize: '13px'
                                    },
                                    icon: startIndex === 0 && index === 0 ? {
                                        path: "M 0,0 C -4.5,-13.5 -13.5,-15.3 -13.5,-24.3 A 13.5,13.5 0 1,1 13.5,-24.3 C 13.5,-15.3 4.5,-13.5 0,0 z",
                                        fillColor: '#00be95',
                                        fillOpacity: 1,
                                        strokeColor: '#00a380',
                                        strokeWeight: 2,
                                        scale: 0.9,
                                        anchor: new window.google.maps.Point(0, 0),
                                        labelOrigin: new window.google.maps.Point(0, -23)
                                    } : coloredIcon(orderedArray[startIndex + index].label, selectedRoute.assignedInspector.assignedColor),
                                    map: mapInstance,
                                    soloUrls: orderedArray[startIndex + index].soloUrls
                                });
                                
                                const inspectionData = getInspectionData(startIndex + index);
                                const isStartOrEnd = startIndex + index === 0 ? 'start' : (startIndex + index === orderedArray.length - 1 ? 'end' : false);
                                
                                const infoWindow = new window.google.maps.InfoWindow({
                                    content: createInfoWindowContent(
                                        orderedArray[startIndex + index].address,
                                        inspectionData,
                                        orderedArray[startIndex + index].soloUrls,
                                        isStartOrEnd
                                    )
                                });
                                
                                // Add click event listener to the marker
                                marker.addListener('click', () => {
                                    // Close any previously opened info window
                                    if (openInfoWindow.current) {
                                        openInfoWindow.current.close();
                                    }
                                    
                                    currentInfoWindow.current = infoWindow;
                                    currentMarker.current = marker;
                                    openInfoWindow.current = infoWindow;
                                    infoWindow.open(mapInstance, marker);
                                });
                                
                                legMarkers.push(marker);
                            });

                            const destinationMarker = new window.google.maps.Marker({
                                position: result.routes[0].legs[result.routes[0].legs.length - 1].end_location,
                                label: {
                                    color: 'white',
                                    fontWeight: 'bold',
                                    text: orderedArray[endIndex].label,
                                    fontSize: '13px'
                                },
                                zIndex: 1000,
                                icon: endIndex === orderedArray.length - 1 ? {
                                    path: "M 0,0 C -4.5,-13.5 -13.5,-15.3 -13.5,-24.3 A 13.5,13.5 0 1,1 13.5,-24.3 C 13.5,-15.3 4.5,-13.5 0,0 z",
                                    fillColor: '#00be95',
                                    fillOpacity: 1,
                                    strokeColor: '#00a380',
                                    strokeWeight: 2,
                                    scale: 0.9,
                                    anchor: new window.google.maps.Point(0, 0),
                                    labelOrigin: new window.google.maps.Point(0, -23)
                                } : coloredIcon(orderedArray[endIndex].label, selectedRoute.assignedInspector.assignedColor),
                                map: mapInstance,
                                soloUrls: orderedArray[endIndex].soloUrls
                            });
                            
                            // Get inspection data for destination marker
                            const destInspectionData = getInspectionData(endIndex);
                            const isDestStartOrEnd = endIndex === 0 ? 'start' : (endIndex === orderedArray.length - 1 ? 'end' : false);
                            
                            // Create InfoWindow for the destinationMarker
                            const destInfoWindow = new window.google.maps.InfoWindow({
                                content: createInfoWindowContent(
                                    orderedArray[endIndex].address,
                                    destInspectionData,
                                    orderedArray[endIndex].soloUrls,
                                    isDestStartOrEnd
                                )
                            });

                            // Add click event listener to the destinationMarker
                            destinationMarker.addListener('click', () => {
                                // Close any previously opened info window
                                if (openInfoWindow.current) {
                                    openInfoWindow.current.close();
                                }
                                
                                currentInfoWindow.current = destInfoWindow;
                                currentMarker.current = destinationMarker;
                                openInfoWindow.current = destInfoWindow;
                                destInfoWindow.open(mapInstance, destinationMarker);
                            });

                            legMarkers.push(destinationMarker);
            
                            const directionsRenderer = new window.google.maps.DirectionsRenderer({
                                map: mapInstance,
                                suppressMarkers: true, // This is important! It will hide default markers.
                                directions: result,
                                polylineOptions: {
                                    strokeColor: selectedRoute.assignedInspector.assignedColor,
                                    strokeOpacity: 1,
                                    strokeWeight: 5
                                }
                            });
            
                            // Add the newly created renderers and markers to the useRef collections
                            allRenderers.current.push(directionsRenderer);
                            legMarkers.forEach(marker => allMarkers.current.push(marker));
                    
                            fetchSegmentedRoutes(endIndex);
                        } else {
                            console.error(`Error fetching directions for segment starting at ${startIndex}: ${status}`);
                            if (status == 'ZERO_RESULTS') {
                                alert('Map preview not available for this route.')
                            }
                        }
                    });
                }
                
                // Start processing the segments.
                fetchSegmentedRoutes();
            }
        } else {
            // Clear existing renderers and markers
            allRenderers.current.forEach(renderer => renderer.setMap(null));
            allMarkers.current.forEach(marker => marker.setMap(null));
            allRenderers.current = [];
            allMarkers.current = [];

            // Clear directions
            setDirections(null);

            // should trigger other useEffect to add markers
            let temp = removeRouteLines + 1
            setRemoveRouteLines(temp)
        }
    }, [props.selectedInspector, props.optimizedRoutes, mapInstance]);

    // Show all inspector markers when no inspector is selected
    useEffect(() => {
        if (!props.selectedInspector && props.optimizedRoutes && mapInstance) {
            // Clear existing renderers and markers
            allRenderers.current.forEach(renderer => renderer.setMap(null));
            allMarkers.current.forEach(marker => marker.setMap(null));
            allRenderers.current = [];
            allMarkers.current = [];

            const newMarkers = [];

            // Show all inspector markers without routes
            props.optimizedRoutes.forEach((route, index) => {
                const inspector = route.assignedInspector;
                const inspectorColor = inspector.assignedColor;
                
                // Use the first route point as the inspector's position
                const firstRoutePoint = route.routeOrder[0];
                if (firstRoutePoint) {
                    newMarkers.push({
                        id: `inspector-${inspector.INSPECTOR_ID}`,
                        position: { lat: firstRoutePoint.coordinates[0], lng: firstRoutePoint.coordinates[1] },
                        title: `${inspector.INSPECTOR_ID} - ${inspector.Street}`,
                        label: ' ',
                        color: inspectorColor,
                        isInspector: true,
                        inspector: inspector,
                        route: route
                    });
                }
            });

            setMarkers(newMarkers);
        }
    }, [props.selectedInspector, props.optimizedRoutes, mapInstance]);

    // Calculate map center and zoom
    let mapCenter = { lat: 40.7128, lng: -74.0060 }; // NYC center
    let mapZoom = 10;

    if (markers.length > 0) {
        if (props.selectedInspector) {
            // Center on the selected inspector's route
            mapCenter = markers[0].position;
            mapZoom = 12;
        } else {
            // Center on all inspectors
            const lats = markers.map(m => m.position.lat);
            const lngs = markers.map(m => m.position.lng);
            mapCenter = {
                lat: (Math.min(...lats) + Math.max(...lats)) / 2,
                lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
            };
            mapZoom = 10;
        }
    }

    // SVG Icon for markers
    const svgIcon = {
        path: "M 0,0 C -4.5,-13.5 -13.5,-15.3 -13.5,-24.3 A 13.5,13.5 0 1,1 13.5,-24.3 C 13.5,-15.3 4.5,-13.5 0,0 z",
        fillColor: '#00be95',
        fillOpacity: 1,
        strokeColor: '#00a380',
        strokeWeight: 2,
        scale: 0.9,
        anchor: new window.google.maps.Point(0, 0),
        labelOrigin: new window.google.maps.Point(0, -23)
    };

    // Custom colored icon function
    const coloredIcon = (label, color) => {
        const SVG_PIN = `
            M 0,0 C -4.5,-13.5 -13.5,-15.3 -13.5,-24.3 A 13.5,13.5 0 1,1 13.5,-24.3 C 13.5,-15.3 4.5,-13.5 0,0 z
        `;

        // Function to darken the color
        const darkenColor = (color, amount) => {
            let usePound = false;
            if (color[0] === "#") {
                color = color.slice(1);
                usePound = true;
            }

            let num = parseInt(color, 16);
            let r = (num >> 16) + amount;
            let g = ((num >> 8) & 0x00ff) + amount;
            let b = (num & 0x0000ff) + amount;

            r = r > 255 ? 255 : r < 0 ? 0 : r;
            g = g > 255 ? 255 : g < 0 ? 0 : g;
            b = b > 255 ? 255 : b < 0 ? 0 : b;

            return (usePound ? "#" : "") + (r << 16 | g << 8 | b).toString(16).padStart(6, "0");
        };

        const darkerColor = darkenColor(color, -30);

        return {
            path: SVG_PIN,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: darkerColor,
            strokeWeight: 2,
            scale: 0.9,
            anchor: new window.google.maps.Point(0, 0),
            labelOrigin: new window.google.maps.Point(0, -23)
        };
    };

    return (
        <GoogleMap
            onLoad={map => setMapInstance(map)}
            zoom={mapZoom}
            center={mapCenter}
            options={{ styles: mapStyles }}
            mapContainerStyle={{ width: "100%", height: "100%" }}
        >
            {/* Render markers when no inspector is selected */}
            {!props.selectedInspector && markers.map((marker) => (
                <Marker
                    key={marker.id}
                    position={marker.position}
                    label={{
                        text: marker.label,
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        fontSize: marker.isInspector ? '16px' : '13px'
                    }}
                    icon={coloredIcon(marker.label, marker.color)}
                    onClick={() => setSelectedLocation(marker)}
                />
            ))}

            {/* Info Window */}
            {selectedLocation && (
                <InfoWindow
                    position={selectedLocation.position}
                    onCloseClick={() => {
                        setSelectedLocation(null);
                    }}
                >
                    <div>
                        <p className="infowindow-text">
                            {selectedLocation.isInspector 
                                ? `${selectedLocation.inspector.INSPECTOR_ID} (Inspector)`
                                : selectedLocation.address
                            }
                        </p>
                    </div>
                </InfoWindow>
            )}
        </GoogleMap>
    );
}

function MapInterface(props) {
    const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);

    useEffect(() => {
        if (window.google && window.google.maps) {
            // Google Maps API is loaded and ready to use
            setIsGoogleMapsReady(true);
        } else {
            const handleLoad = () => {
                setIsGoogleMapsReady(true);
            };
            window.addEventListener("load", handleLoad);

            return () => {
                window.removeEventListener("load", handleLoad);
            };
        }
    }, []);

    return (
        <div className="map pt-14">
            {isGoogleMapsReady && <Map {...props} />}
        </div>
    );
}

export default MapInterface; 